import { createContext, useContext, useEffect, useState, type ReactNode, type MouseEvent } from "react";

const RouteCtx = createContext<{ path: string; query: URLSearchParams }>({ path: "/", query: new URLSearchParams() });

function current() {
  return { path: location.pathname, query: new URLSearchParams(location.search) };
}

export function navigate(to: string, opts: { replace?: boolean } = {}) {
  if (opts.replace) history.replaceState(null, "", to);
  else history.pushState(null, "", to);
  window.dispatchEvent(new Event("rca:navigate"));
}

/** Update query params without losing the path */
export function setQuery(patch: Record<string, string | null | undefined>, opts: { replace?: boolean } = { replace: true }) {
  const q = new URLSearchParams(location.search);
  for (const [k, v] of Object.entries(patch)) {
    if (v === null || v === undefined || v === "") q.delete(k);
    else q.set(k, v);
  }
  const s = q.toString();
  navigate(location.pathname + (s ? "?" + s : ""), opts);
}

export function RouterProvider({ children }: { children: ReactNode }) {
  const [loc, setLoc] = useState(current);
  useEffect(() => {
    const on = () => setLoc(current());
    window.addEventListener("popstate", on);
    window.addEventListener("rca:navigate", on);
    return () => {
      window.removeEventListener("popstate", on);
      window.removeEventListener("rca:navigate", on);
    };
  }, []);
  return <RouteCtx.Provider value={loc}>{children}</RouteCtx.Provider>;
}

export const useRoute = () => useContext(RouteCtx);

/** Match "/team/members/:id" against a path; returns params or null */
export function match(pattern: string, path: string): Record<string, string> | null {
  const p = pattern.split("/").filter(Boolean);
  const a = path.split("/").filter(Boolean);
  if (p.length !== a.length) return null;
  const out: Record<string, string> = {};
  for (let i = 0; i < p.length; i++) {
    if (p[i].startsWith(":")) out[p[i].slice(1)] = decodeURIComponent(a[i]);
    else if (p[i] !== a[i]) return null;
  }
  return out;
}

export function Link({ to, children, className, ...rest }: { to: string; children: ReactNode; className?: string } & Record<string, any>) {
  const onClick = (e: MouseEvent<HTMLAnchorElement>) => {
    if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    e.preventDefault();
    navigate(to);
  };
  return (
    <a href={to} onClick={onClick} className={className} {...rest}>
      {children}
    </a>
  );
}
